import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useRedoTask,
  newIdempotencyKey,
  familyKeys,
  type DayTasksResponse,
  type MissionsResponse,
  type OccurrenceResponse,
} from '@kidshome/api-contract';
import { invalidateHeroSurfaces } from '@/entities/hero';

/**
 * `redo-task` — the kid's "Try again" on a REJECTED chore. Re-submission always lands in
 * `pending_approval` (a grown-up looks again before any coins), so unlike `complete-task` there is no
 * balance to bump — only the chore's status flips. We optimistically:
 *   1. cancel in-flight day + single-occurrence queries and snapshot them,
 *   2. flip the occurrence to `pending_approval` in BOTH caches (and clear the stale review note),
 *   3. carry an `Idempotency-Key` so an offline replay never double-submits,
 *   4. on error roll both caches back, and on settle invalidate so the server reconciles real state.
 *
 * Two caches because two surfaces read different keys: the TaskBoard reads the day list, the chore
 * detail screen reads the single occurrence — both should reflect the retry instantly.
 */
export interface RedoTaskContext {
  readonly date: string;
  readonly assigneeId: string;
}

interface Snapshot {
  day: DayTasksResponse | undefined;
  missions: MissionsResponse | undefined;
  occurrence: OccurrenceResponse | undefined;
  dayKey: readonly unknown[];
  missionsKey: readonly unknown[];
  occurrenceKey: readonly unknown[];
  /** The assignee THIS mutation was issued for — pinned so `onSettled` reconciles the right week agenda
   * even if the caller has since re-rendered with a different date/assignee (e.g. the kid navigated to
   * another day/week while the request was still in flight). */
  assigneeId: string;
}

function applyRedo(day: DayTasksResponse, occurrenceId: string): DayTasksResponse {
  return {
    ...day,
    groups: day.groups.map((g) => ({
      ...g,
      occurrences: g.occurrences.map((o) =>
        o.id === occurrenceId ? { ...o, status: 'pending_approval', reviewNote: null } : o,
      ),
    })),
  };
}

/** The Missions-lane analogue of {@link applyRedo} — a rejected mission re-submitted from Lane B. */
function applyRedoMissions(missions: MissionsResponse, occurrenceId: string): MissionsResponse {
  return {
    ...missions,
    missions: missions.missions.map((o) =>
      o.id === occurrenceId ? { ...o, status: 'pending_approval', reviewNote: null } : o,
    ),
  };
}

function revertOccurrenceInMissions(
  missions: MissionsResponse,
  occurrenceId: string,
  original: Pick<OccurrenceResponse, 'status' | 'reviewNote'>,
): MissionsResponse {
  return {
    ...missions,
    missions: missions.missions.map((o) => (o.id === occurrenceId ? { ...o, ...original } : o)),
  };
}

/** THIS occurrence's pre-mutation `status` + `reviewNote`, read from the captured snapshot (day-list
 * first, single-occurrence fallback for the cold-day deep-link case) — so error rollback can surgically
 * restore only this row. `undefined` if the occurrence wasn't in either cache. */
function originalRowOf(
  day: DayTasksResponse | undefined,
  missions: MissionsResponse | undefined,
  occurrence: OccurrenceResponse | undefined,
  occurrenceId: string,
): Pick<OccurrenceResponse, 'status' | 'reviewNote'> | undefined {
  if (day) {
    for (const g of day.groups) {
      for (const o of g.occurrences) {
        if (o.id === occurrenceId) return { status: o.status, reviewNote: o.reviewNote };
      }
    }
  }
  if (missions) {
    for (const o of missions.missions) {
      if (o.id === occurrenceId) return { status: o.status, reviewNote: o.reviewNote };
    }
  }
  if (occurrence && occurrence.id === occurrenceId) {
    return { status: occurrence.status, reviewNote: occurrence.reviewNote };
  }
  return undefined;
}

/** Reset ONE occurrence row back to its captured `status`/`reviewNote` inside the *current* day cache,
 * leaving every other row untouched — including a sibling redo that optimistically advanced the same
 * shared day cache while this request was still in flight. */
function revertOccurrenceInDay(
  day: DayTasksResponse,
  occurrenceId: string,
  original: Pick<OccurrenceResponse, 'status' | 'reviewNote'>,
): DayTasksResponse {
  return {
    ...day,
    groups: day.groups.map((g) => ({
      ...g,
      occurrences: g.occurrences.map((o) => (o.id === occurrenceId ? { ...o, ...original } : o)),
    })),
  };
}

export function useRedoTaskAction(ctx: RedoTaskContext): {
  /** Re-submit. A photo-required chore passes the FRESH `photoProofKey` from a new capture/upload. */
  redo: (occurrenceId: string, photoProofKey?: string) => void;
  /** True while ANY redo is in flight. */
  isPending: boolean;
  /** True while THIS occurrence's redo is in flight (per-row pending). */
  isOccurrencePending: (occurrenceId: string) => boolean;
  /** The last redo failure (so the caller can surface it), or null. Cleared on the next try. */
  error: unknown;
} {
  const qc = useQueryClient();
  const dayKey = familyKeys.day(ctx.assigneeId, ctx.date);
  const missionsKey = familyKeys.missions(ctx.assigneeId);

  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());

  const mutation = useRedoTask<Snapshot>({
    onMutate: async ({ occurrenceId }) => {
      setPendingIds((prev) => new Set(prev).add(occurrenceId));
      const occurrenceKey = familyKeys.occurrence(occurrenceId);
      await Promise.all([
        qc.cancelQueries({ queryKey: dayKey }),
        qc.cancelQueries({ queryKey: missionsKey }),
        qc.cancelQueries({ queryKey: occurrenceKey }),
      ]);
      const day = qc.getQueryData<DayTasksResponse>(dayKey);
      const missions = qc.getQueryData<MissionsResponse>(missionsKey);
      const occurrence = qc.getQueryData<OccurrenceResponse>(occurrenceKey);

      if (day) {
        qc.setQueryData<DayTasksResponse>(dayKey, applyRedo(day, occurrenceId));
      }
      if (missions) {
        qc.setQueryData<MissionsResponse>(missionsKey, applyRedoMissions(missions, occurrenceId));
      }
      if (occurrence) {
        qc.setQueryData<OccurrenceResponse>(occurrenceKey, {
          ...occurrence,
          status: 'pending_approval',
          reviewNote: null,
        });
      }
      return {
        day,
        missions,
        occurrence,
        dayKey,
        missionsKey,
        occurrenceKey,
        assigneeId: ctx.assigneeId,
      };
    },
    onError: (_err, { occurrenceId }, context) => {
      if (!context) return;
      // Surgically undo ONLY this occurrence's optimistic flip inside the LATEST day cache. Restoring the
      // whole captured `context.day` here would clobber a SIBLING redo that optimistically advanced the
      // same shared day cache while this request was in flight (a kid tapping "Try again" on two rejected
      // chores in quick succession) — the sibling's row would flash back to `rejected` until `onSettled`
      // refetches. Every other row, including that in-flight sibling, is left exactly as it stands.
      const original = originalRowOf(
        context.day,
        context.missions,
        context.occurrence,
        occurrenceId,
      );
      const currentDay = qc.getQueryData<DayTasksResponse>(context.dayKey);
      if (currentDay && original) {
        qc.setQueryData<DayTasksResponse>(
          context.dayKey,
          revertOccurrenceInDay(currentDay, occurrenceId, original),
        );
      }
      const currentMissions = qc.getQueryData<MissionsResponse>(context.missionsKey);
      if (currentMissions && original) {
        qc.setQueryData<MissionsResponse>(
          context.missionsKey,
          revertOccurrenceInMissions(currentMissions, occurrenceId, original),
        );
      }
      // The single-occurrence cache is keyed to THIS occurrence alone (never shared with a sibling), so
      // restoring its captured snapshot is both safe and precise.
      qc.setQueryData(context.occurrenceKey, context.occurrence);
    },
    onSettled: (_data, _err, { occurrenceId }, context) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(occurrenceId);
        return next;
      });
      // Reconcile the day/week THIS mutation was actually issued for — read from the pinned `context`
      // (captured by `onMutate` at mutate-time), NOT the outer-scope `dayKey`/`ctx.assigneeId` closures,
      // which TanStack Query silently rebinds to the LATEST render's props on every render (incl. while
      // this mutation is still in flight). Without this, navigating to a different day/week before the
      // request settles reconciles the WRONG day's cache.
      const dayKeyToInvalidate = context?.dayKey ?? dayKey;
      const missionsKeyToInvalidate = context?.missionsKey ?? missionsKey;
      const assigneeIdToInvalidate = context?.assigneeId ?? ctx.assigneeId;
      // The server is authoritative: reconcile both surfaces from the source of truth.
      void qc.invalidateQueries({ queryKey: dayKeyToInvalidate });
      // Re-submitting a rejected MISSION keeps it in the lane (rejected → pending) — reconcile it too.
      void qc.invalidateQueries({ queryKey: missionsKeyToInvalidate });
      void qc.invalidateQueries({ queryKey: familyKeys.occurrence(occurrenceId) });
      // Re-submitting flips the calendar's state pill (rejected → pending_approval), so reconcile the
      // week agenda for this assignee too (its own query isn't covered by the day key above).
      void qc.invalidateQueries({ queryKey: familyKeys.weeks(assigneeIdToInvalidate) });
      // A re-submission re-enters the approval flow (and, once approved, advances the shared quest bar
      // + grows the hero's bond), so reconcile those surfaces too — mirroring the complete-task hook so
      // the cooperative quest card and the live hero presence never read stale after a "Try again".
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
      invalidateHeroSurfaces(qc);
    },
  });

  const redo = useCallback(
    (occurrenceId: string, photoProofKey?: string) => {
      // Clear any prior failure so a retry doesn't keep showing a stale error.
      mutation.reset();
      mutation.mutate({ occurrenceId, photoProofKey, idempotencyKey: newIdempotencyKey() });
    },
    [mutation],
  );

  const isOccurrencePending = useCallback(
    (occurrenceId: string) => pendingIds.has(occurrenceId),
    [pendingIds],
  );

  return {
    redo,
    isPending: pendingIds.size > 0,
    isOccurrencePending,
    error: mutation.error,
  };
}
